import React, { useState, useEffect } from 'react';
import { Globe, MapPin, Check, RefreshCw, Clock, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { timezones } from './task/constants';
import toast from 'react-hot-toast';

interface TimezoneSettingsData {
  default_timezone: string;
  timezone_auto_detected: boolean;
  timezone_detected_at: string | null;
}

export function TimezoneSettings() {
  const [settings, setSettings] = useState<TimezoneSettingsData>({
    default_timezone: 'Europe/Warsaw',
    timezone_auto_detected: false,
    timezone_detected_at: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_settings')
        .select('default_timezone, timezone_auto_detected, timezone_detected_at')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setSettings({
          default_timezone: data.default_timezone || 'Europe/Warsaw',
          timezone_auto_detected: data.timezone_auto_detected || false,
          timezone_detected_at: data.timezone_detected_at,
        });
      } else {
        await autoDetectTimezone();
      }
    } catch (error) {
      console.error('Error loading timezone settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const detectTimezoneFromBrowser = (): string => {
    try {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const matchingTimezone = timezones.find(tz => tz.id === browserTimezone);
      if (matchingTimezone) {
        return browserTimezone;
      }
      const offset = new Date().getTimezoneOffset();
      const offsetHours = -offset / 60;

      if (offsetHours >= 1 && offsetHours <= 2) return 'Europe/Warsaw';
      if (offsetHours === 0) return 'Europe/London';
      if (offsetHours === -5) return 'America/New_York';
      if (offsetHours === -8) return 'America/Los_Angeles';
      if (offsetHours === 9) return 'Asia/Tokyo';
      if (offsetHours === 8) return 'Asia/Shanghai';

      return 'Europe/Warsaw';
    } catch {
      return 'Europe/Warsaw';
    }
  };

  const autoDetectTimezone = async () => {
    setDetecting(true);
    try {
      const detectedTimezone = detectTimezoneFromBrowser();

      setSettings(prev => ({
        ...prev,
        default_timezone: detectedTimezone,
        timezone_auto_detected: true,
        timezone_detected_at: new Date().toISOString(),
      }));

      await saveSettings({
        default_timezone: detectedTimezone,
        timezone_auto_detected: true,
        timezone_detected_at: new Date().toISOString(),
      });

      const tzInfo = timezones.find(tz => tz.id === detectedTimezone);
      toast.success(`Wykryto strefę czasową: ${tzInfo?.label || detectedTimezone}`);
    } catch (error) {
      console.error('Error detecting timezone:', error);
      toast.error('Nie udało się wykryć strefy czasowej');
    } finally {
      setDetecting(false);
    }
  };

  const saveSettings = async (newSettings: TimezoneSettingsData) => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: existingSettings } = await supabase
        .from('user_settings')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingSettings) {
        const { error } = await supabase
          .from('user_settings')
          .update({
            default_timezone: newSettings.default_timezone,
            timezone_auto_detected: newSettings.timezone_auto_detected,
            timezone_detected_at: newSettings.timezone_detected_at,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_settings')
          .insert({
            user_id: user.id,
            default_timezone: newSettings.default_timezone,
            timezone_auto_detected: newSettings.timezone_auto_detected,
            timezone_detected_at: newSettings.timezone_detected_at,
          });

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error saving timezone settings:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleTimezoneChange = async (timezoneId: string) => {
    const newSettings = {
      ...settings,
      default_timezone: timezoneId,
      timezone_auto_detected: false,
    };

    setSettings(newSettings);
    setIsDropdownOpen(false);
    setSearchQuery('');

    try {
      await saveSettings(newSettings);
      const tzInfo = timezones.find(tz => tz.id === timezoneId);
      toast.success(`Strefa czasowa zmieniona na: ${tzInfo?.label || timezoneId}`);
    } catch {
      toast.error('Nie udało się zapisać strefy czasowej');
    }
  };

  const getCurrentTime = () => {
    try {
      return new Date().toLocaleTimeString('pl-PL', {
        timeZone: settings.default_timezone,
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '--:--';
    }
  };

  const groupedTimezones = timezones.reduce((acc, tz) => {
    const region = tz.region || 'Inne';
    if (!acc[region]) {
      acc[region] = [];
    }
    acc[region].push(tz);
    return acc;
  }, {} as Record<string, typeof timezones>);

  const filteredGroupedTimezones = Object.entries(groupedTimezones).reduce((acc, [region, tzList]) => {
    const filtered = tzList.filter(tz =>
      tz.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tz.id.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[region] = filtered;
    }
    return acc;
  }, {} as Record<string, typeof timezones>);

  const selectedTimezone = timezones.find(tz => tz.id === settings.default_timezone);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center space-x-3 mb-6">
        <Globe className="w-6 h-6 text-blue-600" />
        <div>
          <h2 className="text-xl font-semibold">Strefa czasowa</h2>
          <p className="text-sm text-gray-500">Ustaw domyślną strefę czasową dla harmonogramu postów</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Clock className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-900">Aktualny czas w Twojej strefie</p>
              <p className="text-2xl font-bold text-blue-700">{getCurrentTime()}</p>
            </div>
          </div>
          {settings.timezone_auto_detected && (
            <div className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
              <Check className="w-3 h-3" />
              <span>Wykryto automatycznie</span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Domyślna strefa czasowa
          </label>
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            >
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <span className="text-gray-900">{selectedTimezone?.label || settings.default_timezone}</span>
              </div>
              <svg className={`w-5 h-5 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isDropdownOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-hidden">
                <div className="p-2 border-b border-gray-200">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Szukaj strefy czasowej..."
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="overflow-y-auto max-h-72">
                  {Object.entries(filteredGroupedTimezones).map(([region, tzList]) => (
                    <div key={region}>
                      <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider sticky top-0">
                        {region}
                      </div>
                      {tzList.map((tz) => (
                        <button
                          key={tz.id}
                          onClick={() => handleTimezoneChange(tz.id)}
                          className={`w-full px-4 py-2 text-left hover:bg-blue-50 flex items-center justify-between transition-colors ${
                            settings.default_timezone === tz.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                          }`}
                        >
                          <span>{tz.label}</span>
                          {settings.default_timezone === tz.id && (
                            <Check className="w-4 h-4 text-blue-600" />
                          )}
                        </button>
                      ))}
                    </div>
                  ))}
                  {Object.keys(filteredGroupedTimezones).length === 0 && (
                    <div className="px-4 py-8 text-center text-gray-500">
                      Nie znaleziono pasujących stref czasowych
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <button
          onClick={autoDetectTimezone}
          disabled={detecting}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 ${detecting ? 'animate-spin' : ''}`} />
          <span>{detecting ? 'Wykrywanie...' : 'Wykryj automatycznie'}</span>
        </button>

        {settings.timezone_detected_at && (
          <p className="text-xs text-gray-500">
            Ostatnio wykryto: {new Date(settings.timezone_detected_at).toLocaleString('pl-PL')}
          </p>
        )}
      </div>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-amber-900 mb-2">Jak działa strefa czasowa?</h3>
          <ul className="text-sm text-amber-800 space-y-1">
            <li>Domyślna strefa czasowa jest używana przy tworzeniu nowych zadań</li>
            <li>Możesz nadpisać strefę czasową dla konkretnego zadania</li>
            <li>System automatycznie przelicza czas publikacji na UTC</li>
            <li>Posty są publikowane o wskazanej godzinie w wybranej strefie czasowej</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
